import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiRequestError, fetchAuthConfig, fetchDemoLinks, fetchStaffIdentity, signInWithGoogle } from '../lib/api'
import { verifySharedToken } from '../lib/staffSignIn'
import { useStaffToken } from '../lib/staffToken'

/**
 * The staff entrance — the door the customer's home page does NOT have.
 *
 * Splitting the two entrances is the point of this file. Before it, `/` was a
 * page that offered a bride a "start a fitting" button and, directly under it,
 * a password field, a visit list and a box for pasting other people's session
 * tokens. One page cannot be both a shop window and a staff room.
 *
 * Two ways in, drawn from what the API says this deployment actually has
 * (`GET /api/auth/config`) rather than from a build flag:
 *
 *  - **Google**, when a client id is configured. This is the way forward.
 *  - **The shared token**, when `FITTING_ROOM_STAFF_TOKENS` still has one. It
 *    is what every existing boutique is using right now, and it keeps working
 *    until whoever runs the deployment empties that variable. A deployment
 *    mid-migration draws both.
 *
 * A deployment with neither draws neither, and says so — which is a real state:
 * it is what a fresh container with no environment at all looks like, and
 * "nothing happens when I click" is a much worse way to discover it.
 */

/**
 * Google Identity Services, loaded from Google when there is a client id and
 * never otherwise.
 *
 * Deliberately a script tag rather than a dependency. The button is one
 * element and one callback; an npm wrapper around it would be a package to keep
 * current for no behaviour we do not have here. More importantly it means a
 * deployment WITHOUT Google sign-in — which is every deployment today, the test
 * suite and the browser pass included — never reaches out to accounts.google.com
 * at all. A dependency would ship in the bundle regardless.
 */
const GSI_SRC = 'https://accounts.google.com/gsi/client'

interface GsiCredentialResponse {
  credential: string
}

interface GsiApi {
  accounts: {
    id: {
      initialize(options: { client_id: string; callback: (response: GsiCredentialResponse) => void }): void
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void
    }
  }
}

function loadGsi(): Promise<GsiApi> {
  const existing = (window as unknown as { google?: GsiApi }).google
  if (existing?.accounts?.id) return Promise.resolve(existing)
  return new Promise((resolve, reject) => {
    // One tag however many times this mounts: React 19 in StrictMode renders
    // this twice in development, and two copies of the GSI script race each
    // other to define the same global.
    const already = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`)
    const script = already ?? document.createElement('script')
    const done = () => {
      const api = (window as unknown as { google?: GsiApi }).google
      if (api?.accounts?.id) resolve(api)
      else reject(new Error('Google sign-in loaded but did not initialize'))
    }
    script.addEventListener('load', done)
    script.addEventListener('error', () => reject(new Error('Google sign-in could not be loaded')))
    if (!already) {
      script.src = GSI_SRC
      script.async = true
      document.head.appendChild(script)
    } else if (already.getAttribute('data-loaded') === 'true') {
      done()
    }
    script.setAttribute('data-loaded', 'true')
  })
}

function GoogleButton({ clientId, onCredential }: { clientId: string; onCredential: (credential: string) => void }) {
  const host = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  // The callback is read through a ref so a re-render does not re-initialise
  // GSI, which would draw a second button beside the first.
  const latest = useRef(onCredential)
  latest.current = onCredential

  useEffect(() => {
    let cancelled = false
    loadGsi()
      .then((google) => {
        if (cancelled || !host.current) return
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => latest.current(response.credential),
        })
        google.accounts.id.renderButton(host.current, { theme: 'outline', size: 'large', width: 320 })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  if (failed) {
    // A shop with no outbound network to Google still has the shared token, and
    // it is on this same page. Saying which of the two is broken is the whole
    // value of this line.
    return <p className="st-meta">Google sign-in could not be loaded. Check this device&apos;s connection.</p>
  }
  return <div ref={host} />
}

export function StaffSignInRoute() {
  const { token, setToken, setSession } = useStaffToken()
  const navigate = useNavigate()
  const [value, setValue] = useState('')

  const config = useQuery({
    queryKey: ['auth', 'config'] as const,
    queryFn: ({ signal }) => fetchAuthConfig(signal),
    retry: false,
    staleTime: Infinity,
  })

  // 404 unless FITTING_ROOM_DEMO_LINKS is on, which is every deployment that is
  // not a demo. `undefined` renders nothing at all.
  const demo = useQuery({
    queryKey: ['demo', 'links'] as const,
    queryFn: ({ signal }) => fetchDemoLinks(signal),
    retry: false,
    staleTime: Infinity,
  })

  const signIn = useMutation({
    mutationFn: (credential: string) => signInWithGoogle(credential),
    onSuccess: (session) => setSession(session),
  })

  /*
   * The shared token is verified before it is stored.
   *
   * `setToken` is what redirects — the effect below watches `token` — so
   * calling it on submit made the console the place a bad token was
   * discovered, and the console cannot say anything useful about one: it draws
   * its frame, 401s on every query, and shows a banner over whatever the
   * previous credential left in the cache. Asking `/api/auth/me` first keeps
   * the answer on the page that asked the question. See `staffSignIn.ts` for
   * why a refusal and an unreachable server are two different sentences.
   */
  const [refusal, setRefusal] = useState<string | null>(null)
  const adopt = useMutation({
    mutationFn: (typed: string) => verifySharedToken((candidate) => fetchStaffIdentity(candidate), typed),
    onMutate: () => setRefusal(null),
    onSuccess: (verdict, typed) => {
      if (verdict.ok) setToken(typed)
      else setRefusal(verdict.message ?? 'That token was not accepted.')
    },
  })

  // Already holding a credential — go where they were going. `replace` so the
  // back button does not land them on a gate they have just passed.
  useEffect(() => {
    if (token) void navigate('/admin', { replace: true })
  }, [token, navigate])

  const google = config.data?.google ?? null
  const sharedTokenAccepted = config.data?.sharedTokenAccepted ?? false
  const nothingConfigured = config.isSuccess && !google && !sharedTokenAccepted

  return (
    <div className="st-root flex min-h-screen items-center justify-center px-6">
      <div className="st-card w-full max-w-sm p-8">
        <div className="st-eyebrow mb-2">Fitting Room</div>
        <h1 className="st-display mb-1 text-2xl">Staff console</h1>

        {google ? (
          <>
            <p className="st-meta mb-5">Sign in with your boutique Google account.</p>
            <GoogleButton clientId={google.clientId} onCredential={(credential) => signIn.mutate(credential)} />
            {signIn.isError ? (
              <p role="alert" className="mt-4 text-sm text-rose-700">
                {/*
                  The API's own sentence, not a generic one. "jo@x is not on this
                  boutique's staff list" is the entire answer to the only
                  question anyone will ask here, and replacing it with "sign-in
                  failed" would throw that away.
                */}
                {signIn.error instanceof ApiRequestError ? signIn.error.message : 'That sign-in did not work.'}
              </p>
            ) : null}
          </>
        ) : null}

        {google && sharedTokenAccepted ? (
          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1" style={{ background: 'var(--st-rule)' }} />
            <span className="st-eyebrow">or</span>
            <span className="h-px flex-1" style={{ background: 'var(--st-rule)' }} />
          </div>
        ) : null}

        {sharedTokenAccepted ? (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              adopt.mutate(value)
            }}
          >
            <label className="st-eyebrow mb-1.5 block" htmlFor="staff-token">
              Access token
            </label>
            <input
              id="staff-token"
              className="st-input mb-5 w-full"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
            <button type="submit" className="st-btn w-full" disabled={!value.trim() || adopt.isPending}>
              {adopt.isPending ? 'Checking\u2026' : 'Open console'}
            </button>
            {refusal ? (
              <p role="alert" className="mt-4 text-sm text-rose-700">
                {refusal}
              </p>
            ) : null}
          </form>
        ) : null}

        {nothingConfigured ? (
          // Neither way in. A real state — a container deployed with no
          // environment — and one that is otherwise discovered as a page where
          // nothing happens.
          <p className="st-meta">
            This deployment has no staff credential configured. Set FITTING_ROOM_GOOGLE_CLIENT_ID and
            FITTING_ROOM_STAFF_DIRECTORY, or FITTING_ROOM_STAFF_TOKENS.
          </p>
        ) : null}

        {demo.data ? (
          /*
            The demo board, moved here off the customer's home page.
            `GET /api/demo/links` 404s unless FITTING_ROOM_DEMO_LINKS is set, so
            this is invisible in every deployment that is not a demo — but where
            it IS on it publishes customer links, and a page a bride lands on was
            the wrong place for it however guarded.
          */
          <section className="mt-8 border-t pt-6" style={{ borderColor: 'var(--st-rule)' }}>
            <h2 className="mb-1 text-sm font-semibold">Demo links</h2>
            <p className="st-meta mb-3">
              The seeded tenant, reissued on every restart. Off unless FITTING_ROOM_DEMO_LINKS is set.
            </p>
            <ul className="space-y-1 text-sm">
              {demo.data.links.map((link) => (
                <li key={link.path}>
                  <Link className="underline underline-offset-4" to={link.path}>
                    {link.customerName} · {link.activity}
                  </Link>
                </li>
              ))}
            </ul>
            {demo.data.staffToken ? (
              <button type="button" className="st-btn-quiet mt-3" onClick={() => setToken(demo.data?.staffToken ?? '')}>
                Use the demo staff token
              </button>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  )
}
